// ============================================================================
// CUSTOM HOOKS FOR PLACE ORDER MODAL STATE MANAGEMENT
// ============================================================================

import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  fetchSites,
  selectSites,
  selectSiteLoading,
} from "../../site/siteSlice";
import { ProductData, OrderDetailData, OrderCreationState } from "./types";
import {
  createDefaultProductData,
  createDefaultOrderDetailData,
  transformExistingProducts,
} from "./utils";

// ============================================================================
// SITE MANAGEMENT HOOK
// ============================================================================

export const useSiteManagement = (
  siteId: string = "",
  customerId: string = "",
  editMode: boolean,
  existingOrder: any,
  isOpen: boolean
) => {
  const dispatch = useAppDispatch();
  const sites = useAppSelector(selectSites);
  const [selectedSiteId, setSelectedSiteId] = useState<string>("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [siteSearch, setSiteSearch] = useState("");
  const [isEditingSite, setIsEditingSite] = useState(false);
  const [siteData, setSiteData] = useState<any>(null);
  const loading = useAppSelector(selectSiteLoading);
  const [fetched, setFetched] = useState(false);

  // Handle site fetching based on available IDs
  useEffect(() => {
    if (!isOpen) return;

    console.log(
      "============================console from hook======================"
    );

    console.log("site ========>", sites);

    console.log("customer id ========>", customerId);
    console.log("site id ========>", siteId);

    console.log("existingOrder customer id ========>", existingOrder);
    console.log("existingOrder site id ========>", existingOrder);

    if (editMode && existingOrder?.customer_id && existingOrder?.site_id) {
      dispatch(
        fetchSites({
          customerId: existingOrder.customer_id,
          siteId: existingOrder.site_id,
        })
      );
    } else {
      //  dispatch(fetchSites({ customerId: customerId , siteId:siteId }));
    }
  }, [editMode]);

  // Set initial site and customer IDs
  useEffect(() => {
    if (editMode && existingOrder) {
      // In edit mode, use existingOrder data
      setSelectedSiteId(existingOrder.site_id || "");
      setSelectedCustomerId(existingOrder.customer_id || "");
    } else {
      // In create mode, use props
      setSelectedSiteId(siteId || "");
      setSelectedCustomerId(customerId || "");
    }
  }, [editMode, existingOrder]);

  // Handle site data matching and display logic
  useEffect(() => {
    if (sites?.length > 0) {
      console.log("=== SITE DATA MATCHING LOGIC ===");
      console.log("Sites received:", sites.length);
      console.log("Sites data:", sites);

      let matchedSite = null;

      if (editMode && existingOrder) {
        // Scenario 3: Edit order - both IDs available
        console.log(
          "Scenario Edit mode: Looking for site with ID:",
          existingOrder.site_id
        );
        matchedSite = sites.find((site) => site?.id === existingOrder.site_id);

        // If we have both IDs, we should get exactly one site
        if (sites.length === 1 && sites[0].id === existingOrder.site_id) {
          console.log("Scenario  ✅ Found exact site match for edit mode");
          matchedSite = sites[0];
        }
      } else if (!editMode) {
        if (customerId && siteId) {
          // Scenario 3: Create order with both IDs (from site list)
          console.log(
            "Scenario  Create mode with both IDs: Looking for site with ID:",
            siteId
          );
          matchedSite = sites.find((site) => site?.id === siteId);

          // If we have both IDs, we should get exactly one site
          if (sites.length === 1 && sites[0].id === siteId) {
            console.log(
              "Scenario ✅ Found exact site match for create mode with both IDs"
            );
            matchedSite = sites[0];
          }
        } else if (customerId && !siteId) {
          // Scenario 1: Create order with customer ID only (from customer list)
          console.log(
            "Scenario Create mode with customer ID only: Found",
            sites.length,
            "sites for customer"
          );
          // dispatch(fetchSites({ customerId }));
          // Don't auto-select a site, let user choose from dropdown
          matchedSite = null;
        } else if (!customerId && siteId) {
          // Scenario 2: Create order with site ID only (from site list)
          console.log(
            "Scenario Create mode with site ID only: Looking for site with ID:",
            siteId
          );
          matchedSite = sites.find((site) => site?.id === siteId);

          // If we have site ID, we should get exactly one site
          if (sites.length === 1 && sites[0].id === siteId) {
            console.log(
              "Scenario ✅ Found exact site match for create mode with site ID only"
            );
            matchedSite = sites[0];
          }
        } else {
          // Scenario 4: No IDs available - show all sites in dropdown
          console.log(
            "Scenario No IDs available: Showing all",
            sites.length,
            "sites in dropdown"
          );
          matchedSite = null;
        }
      }

      console.log("Scenario Final matched site:", matchedSite);
      setSiteData(matchedSite);
    }
  }, [sites, siteId, customerId, editMode, existingOrder]);

  // Set site data when available
  useEffect(() => {
    if (siteData) {
      setSelectedSiteId(siteData?.id);
      setSelectedCustomerId(
        siteData?.customerId || siteData.customer?.customerId || ""
      );
      setIsEditingSite(false);
    }
  }, [siteData]);

  // Fallback: If we have siteId and customerId but no siteData, try to find it
  useEffect(() => {
    if (!siteData && sites?.length > 0 && siteId && customerId) {
      console.log("Fallback: Looking for site with ID:", siteId);
      const foundSite = sites.find((site) => site?.id === siteId);
      if (foundSite) {
        console.log("Fallback: Found site:", foundSite);
        setSiteData(foundSite);
        setSelectedSiteId(foundSite.id);
        setSelectedCustomerId(
          foundSite.customerId || foundSite.customer?.customerId || ""
        );
        setIsEditingSite(false);
      }
    }
  }, [sites, siteId, customerId, siteData]);

  return {
    sites,
    selectedSiteId,
    selectedCustomerId,
    siteSearch,
    isEditingSite,
    siteData,
    setSelectedSiteId,
    setSelectedCustomerId,
    setSiteSearch,
    setIsEditingSite,
    setSiteData,
  };
};

// ============================================================================
// PRODUCT MANAGEMENT HOOK
// ============================================================================

export const useProductManagement = (
  editMode: boolean,
  existingOrder: any,
  isOpen: boolean
) => {
  const [productData, setProductData] = useState<ProductData>(
    createDefaultProductData()
  );
  const [products, setProducts] = useState<ProductData[]>([]);

  // Initialize edit mode data
  useEffect(() => {
    if (editMode && existingOrder && isOpen) {
      console.log("=== EDIT MODE INITIALIZATION ===");
      console.log("Existing order:", existingOrder);

      // Set products if they exist
      if (existingOrder.products && existingOrder.products.length > 0) {
        const transformedProducts = transformExistingProducts(
          existingOrder.products
        );
        console.log("=== SETTING TRANSFORMED PRODUCTS ===");
        console.log("Transformed products:", transformedProducts);
        console.log("First product frame:", transformedProducts[0]?.frame);
        console.log("First product panel:", transformedProducts[0]?.panel);
        setProducts(transformedProducts);
      } else {
        // If no products exist, initialize with default product
        console.log("No existing products found, initializing with default");
        setProducts([createDefaultProductData()]);
      }
    }
  }, [editMode, existingOrder, isOpen]);

  // Sync productData with products
  useEffect(() => {
    if (
      products.length > 0 &&
      (!productData.id || productData.id !== products[0].id)
    ) {
      console.log("Syncing productData with first product from products array");
      setProductData(products[0]);
    }
  }, [products, productData.id]);

  const handleProductDataUpdate = (newData: any) => {
    console.log("ProductSection setData called with:", newData);

    // Handle the case where newData contains products array
    if (newData && newData.products && Array.isArray(newData.products)) {
      console.log("Setting products from newData.products:", newData.products);
      setProducts(newData.products);
      // Also update the single product data for backward compatibility
      if (newData.products.length > 0) {
        setProductData(newData.products[0]);
      }
    } else if (newData && newData.id) {
      // Handle single product update
      setProductData(newData);
      setProducts([newData]);
    } else if (Array.isArray(newData)) {
      // Handle case where newData is directly an array of products
      console.log("Setting products from newData array:", newData);
      setProducts(newData);
      if (newData.length > 0) {
        setProductData(newData[0]);
      }
    } else {
      console.log("Unknown data structure received:", newData);
    }
  };

  const handleProductSectionSubmit = (orderData: any) => {
    console.log("ProductSection onSubmit called with:", orderData);
    if (orderData.products && Array.isArray(orderData.products)) {
      setProducts(orderData.products);
    }
  };

  return {
    productData,
    products,
    setProductData,
    setProducts,
    handleProductDataUpdate,
    handleProductSectionSubmit,
  };
};

// ============================================================================
// ORDER DETAILS MANAGEMENT HOOK
// ============================================================================

export const useOrderDetailsManagement = (
  editMode: boolean,
  existingOrder: any,
  siteData: any
) => {
  const [orderDetailData, setOrderDetailData] = useState<OrderDetailData>();

  // Initialize order details for edit mode
  useEffect(() => {
    if (editMode && existingOrder) {
      // Set order details
      if (existingOrder.totalAmount) {
        setOrderDetailData((prev) => ({
          ...prev,
          totalAmount: existingOrder.totalAmount.toString(),
          dueDate:
            existingOrder.dueDate || new Date().toISOString().split("T")[0],
          notes: existingOrder.notes || "",
          priority: existingOrder.priority || "medium",
          paymentStatus: existingOrder.paymentStatus || "pending",
        }));
      }
    } else if (siteData) {
      // Create mode: initialize with site data
      try {
        const defaultOrderData = createDefaultOrderDetailData(siteData);
        setOrderDetailData(defaultOrderData);
      } catch (error) {
        console.error("Error creating default order data:", error);
      }
    }
  }, [editMode, existingOrder, siteData]);

  const handleOrderDetailsUpdate = (order: any) => {
    console.log("Order detail from PlaceOrderModal", order);
    setOrderDetailData((prev) => ({
      ...prev,
      ...order,
    }));
  };

  return {
    orderDetailData,
    setOrderDetailData,
    handleOrderDetailsUpdate,
  };
};

// ============================================================================
// ORDER CREATION STATE HOOK
// ============================================================================

export const useOrderCreationState = () => {
  const [orderCreationState, setOrderCreationState] =
    useState<OrderCreationState>({
      isLoading: false,
      error: null,
      success: null,
    });

  return {
    orderCreationState,
    setOrderCreationState,
  };
};
